import { Injectable } from '@angular/core';
import { ChatContextRef } from 'lib-trend-core';
import { Observable, Subject } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { ChatContextRefPickerDialogComponent } from './chat-context-ref-picker-dialog.component';
import { ChatDialogService } from '../services/chat-dialog.service';

/** A reference chosen for a specific conversation (null channelId = DM draft). */
export interface ChatContextRefSelection {
  /** Conversation the reference was picked for — `null` when unknown/draft. */
  channelId: string | null;
  reference: ChatContextRef;
}

/**
 * Work-reference attachment seam (SPEC-2026-07-internal-chat, T-22 / RF-35).
 *
 * The picker lives here instead of inside the composer so any chat surface can
 * offer "attach a work reference" without duplicating the dialog. Two ways to
 * consume a selection:
 *
 * - `openPicker(channelId)` → observable that emits the chosen reference (or
 *   `null` when cancelled) once. Preferred for a direct call site.
 * - `selection$` → hot queue of every confirmed selection, for a surface that
 *   is not the caller (the composer subscribes to this and attaches the
 *   reference to the next outgoing message).
 */
@Injectable({ providedIn: 'root' })
export class ChatContextRefService {

  private readonly selectionSubject = new Subject<ChatContextRefSelection>();

  /** Confirmed selections, in order. Never completes. */
  readonly selection$: Observable<ChatContextRefSelection> = this.selectionSubject.asObservable();

  constructor(private readonly dialog: ChatDialogService) { }

  /**
   * Opens the picker. Emits ONCE: the chosen reference or `null` when the user
   * cancels. A confirmed reference is also pushed onto `selection$`.
   */
  openPicker(channelId: string | null = null): Observable<ChatContextRef | null> {
    return this.dialog
      .open(ChatContextRefPickerDialogComponent, { width: '520px', maxWidth: '95vw' })
      .afterClosed()
      .pipe(
        take(1),
        map((reference: ChatContextRef | null | undefined) => reference ?? null),
        tap((reference) => {
          if (reference) this.selectionSubject.next({ channelId, reference });
        }),
      );
  }
}
